import React, { useMemo } from "react";
import "./CatMouseChase.css";

const DEFAULT_ANIMALS = ["🐭", "🐱", "🐰", "🦊", "🐶", "🐯", "🦁", "🐼", "🐻", "🐸", "🐵", "🦄"];

// Simple deterministic PRNG (mulberry32)
function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function pickTwoDifferent(list, rand) {
  const safe = Array.isArray(list) ? list.filter(Boolean) : [];
  if (safe.length === 0) return ["🐭", "🐱"];
  if (safe.length === 1) return [safe[0], safe[0]];
  const a = safe[Math.floor(rand() * safe.length)];
  let b = a;
  let guard = 0;
  while (b === a && guard < 10) {
    b = safe[Math.floor(rand() * safe.length)];
    guard += 1;
  }
  return [a, b];
}

export default function CatMouseChase({
  className = "",
  style = {},
  baseDurationSec = 9,
  laneCount = 10,
  seed = 1337,
  animals = DEFAULT_ANIMALS,
  pairs,
  minTopPct = 6,
  maxTopPct = 92,
  minOpacity = 0.82,
  maxOpacity = 1,
} = {}) {
  const lanes = useMemo(() => {
    const safeCount = Math.max(1, Math.min(40, Number(laneCount) || 10));
    return Array.from({ length: safeCount }).map((_, idx) => {
      const laneSeed = ((Number(seed) || 0) + (idx + 1) * 9973) >>> 0;
      const rand = mulberry32(laneSeed);

      const minTop = Math.max(0, Math.min(100, Number(minTopPct) || 0));
      const maxTop = Math.max(minTop, Math.min(100, Number(maxTopPct) || 100));
      const top = minTop + Math.floor(rand() * Math.max(1, maxTop - minTop)); // %
      const size = 18 + Math.floor(rand() * 16); // px
      const duration = (Number(baseDurationSec) || 9) + Math.round(rand() * 45) / 10; // seconds
      const delay = -(Math.round(rand() * 120) / 10); // negative to stagger immediately
      const opMin = Math.max(0, Math.min(1, Number(minOpacity)));
      const opMax = Math.max(opMin, Math.min(1, Number(maxOpacity)));
      const opacity = opMin + rand() * (opMax - opMin);
      const reverse = idx % 2 === 1;

      let prey = "🐭";
      let chaser = "🐱";

      if (Array.isArray(pairs) && pairs.length > 0) {
        const p = pairs[Math.floor(rand() * pairs.length)];
        if (Array.isArray(p) && p.length >= 2) {
          [prey, chaser] = [p[0], p[1]];
        } else if (p && typeof p === "object") {
          prey = p.prey ?? p.a ?? prey;
          chaser = p.chaser ?? p.b ?? chaser;
        }
      } else {
        [prey, chaser] = pickTwoDifferent(animals, rand);
      }

      return {
        key: `lane-${idx}`,
        className: `cm-lane${reverse ? " reverse" : ""}`,
        prey,
        chaser,
        style: {
          "--cm-top": `${top}%`,
          "--cm-size": `${size}px`,
          "--cm-duration": `${duration}s`,
          "--cm-delay": `${delay}s`,
          "--cm-opacity": opacity,
        },
      };
    });
  }, [laneCount, seed, baseDurationSec, animals, pairs, minTopPct, maxTopPct, minOpacity, maxOpacity]);

  return (
    <div
      className={`cat-mouse-chase ${className}`}
      style={{
        ...style,
      }}
      aria-hidden="true"
    >
      {lanes.map((lane) => (
        <div key={lane.key} className={lane.className} style={lane.style}>
          <div className="cm-runner">
            <span className="cm-prey">{lane.prey}</span>
            <span className="cm-chaser">{lane.chaser}</span>
            <span className="cm-dust cm-dust-1">💨</span>
            <span className="cm-dust cm-dust-2">💨</span>
          </div>
        </div>
      ))}
    </div>
  );
}

